import { Box } from '@chakra-ui/layout';
import mixpanel from 'mixpanel-browser';
import React, { useEffect } from 'react';
import Iframe from 'react-iframe';
import { useLocation, useNavigate } from 'react-router';

import {
  CenterLoading,
  type DataDictionaryUrlPathMessage,
  IFrameMessageTypes,
  PagePaths,
  UserTrackingEvents,
  useListenForIFrameMessages,
  WILSON_CLIENT_NAMES,
} from '@revelio/core';
import { useChartTheme } from '@revelio/replots';

import { useBrand } from '../../shared/branding/brand-provider';
import styles from './iframed-pages.module.css';

const DATA_DICTIONARY_BRAND_PARAM = 'brnd';
const DATA_DICTIONARY_WILSON_BRAND = 'wilson';

const DataDictionary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [iframeUrl, setIframeUrl] = React.useState<string>();
  const [iframeLoaded, setIframeLoaded] = React.useState(false);
  const { isNewTheme } = useChartTheme();
  const brand = useBrand();
  useEffect(() => {
    mixpanel.track(UserTrackingEvents.PAGE_VISIT, {
      page: 'Data Dictionary',
      path: location.pathname,
    });
    mixpanel.time_event(UserTrackingEvents.PAGE_LEAVE);
    return () => {
      mixpanel.track(UserTrackingEvents.PAGE_LEAVE, {
        page: 'Data Dictionary',
        path: location.pathname,
      });
    };
  }, [location.pathname]);

  useEffect(() => {
    // Extract the path suffix after '/data-dictionary'
    const pathSuffix = location.pathname.replace(
      `/${PagePaths.DATA_DICTIONARY}`,
      ''
    );

    // Transform the pathSuffix based on the pattern
    let transformedPath = pathSuffix;

    // Check if it matches the pattern /[category]/[sub-category]
    if (/^\/[^/]+\/[^/]+/.test(pathSuffix)) {
      // Replace /[category]/[sub-category] with /[category.html]#[sub-category] for the iframe URL
      transformedPath = pathSuffix.replace(
        /^(\/[^/]+)\/([^/]+)(.*)$/,
        '$1.html#$2$3'
      );
    }
    // Check if it matches just /[category]
    else if (/^\/[^/]+$/.test(pathSuffix)) {
      // Replace /[category] with /[category].html for iframe URL
      transformedPath = `${pathSuffix}.html`;
    }

    const searchParams = new URLSearchParams(location.search);
    if (brand.clientName && WILSON_CLIENT_NAMES.has(brand.clientName)) {
      searchParams.set(
        DATA_DICTIONARY_BRAND_PARAM,
        DATA_DICTIONARY_WILSON_BRAND
      );
    }
    const search = searchParams.toString();
    const querySuffix = search ? `?${search}` : '';

    const hashIndex = transformedPath.indexOf('#');
    const pathBeforeHash =
      hashIndex === -1 ? transformedPath : transformedPath.slice(0, hashIndex);
    const hashFragment =
      hashIndex === -1 ? '' : transformedPath.slice(hashIndex);

    setIframeUrl(
      `https://www.data-dictionary.reveliolabs.com${pathBeforeHash}${querySuffix}${hashFragment}`
    ); // Local Development: setIframeUrl(`http://localhost:45000${pathBeforeHash}${querySuffix}${hashFragment}`);
  }, [location.pathname, location.search, brand.clientName]);

  useListenForIFrameMessages(
    IFrameMessageTypes.DataDictionaryUrlPath,
    ({ data, type }) => {
      const { path, search } = data as DataDictionaryUrlPathMessage;
      if (path && typeof path === 'string' && path !== '/') {
        navigate(
          `/${PagePaths.DATA_DICTIONARY}/${path.startsWith('/') ? path.slice(1) : path}${search}`
        );
      } else {
        console.error('useListenForIFrameMessages: invalid path', {
          data: path,
          type,
        });
      }
    }
  );

  return (
    <Box height="100vh" className={styles.container}>
      <Iframe
        url={iframeUrl ?? ''}
        width="100%"
        height="100%"
        id="ddId"
        position="relative"
        styles={{ border: 'none' }}
        onLoad={() => setIframeLoaded(true)}
      />
      {isNewTheme && !iframeLoaded && (
        <CenterLoading className={styles.loader} />
      )}
    </Box>
  );
};

export default DataDictionary;
